import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  AccountsPayableCreateMode,
  ACCOUNTS_PAYABLE_CREATE_MODE_LABELS
} from '../../../../models/accounts-payable-create.mode';
import { AccountsPayableService } from '../../../../services/accounts-payable.service';
import { SuppliersService } from '../../../../services/suppliers.service';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { StatusService } from '../../../../services/status.service';
import { AuthService } from '../../../../services/auth.service';
import { AccountsPayable, AccountsPayableCreate, AccountsPayableUpdate } from '../../../../models/AccountsPayable';
import { Supplier } from '../../../../models/Supplier';
import { CostCenter } from '../../../../models/CostCenter';
import { Status } from '../../../../models/Status';
import {
  AccountsPayableBatchPreviewRow,
  buildAccountsPayableBatchPayload,
  buildAccountsPayableBatchPreview,
  buildDivisaoLineDescription,
  CostCenterAllocationInput,
  splitAmountByPercentages
} from '../../../../utils/accounts-payable-batch.util';
import {
  buildInstallmentDueDates,
  isWeekendIsoDate,
  toNextBusinessDayIso
} from '../../../../utils/accounts-receivable.utils';

interface CostCenterAllocationRow {
  costCenterId: number | null;
  percent: number;
}

type BatchPreviewSortKey = 'line' | 'document' | 'installment' | 'dueDate' | 'costCenter' | 'amount' | 'percent';

@Component({
  selector: 'app-accounts-payable-form',
  templateUrl: './accounts-payable-form.component.html',
  styleUrls: ['./accounts-payable-form.component.css']
})
export class AccountsPayableFormComponent implements OnInit, OnDestroy {
  // Formulários
  formStep1!: FormGroup; // Informações do documento
  formStep2!: FormGroup; // Valores e vencimento
  formStep3!: FormGroup; // Fornecedor e classificação

  currentStep = 1;
  totalSteps = 3;
  availableTabs = [
    { id: 'step1', label: 'Informações do Documento', icon: 'fas fa-file-alt', required: true, completed: false },
    { id: 'step2', label: 'Valores, parcelas e divisão', icon: 'fas fa-calculator', required: true, completed: false },
    { id: 'step3', label: 'Fornecedor e Classificação', icon: 'fas fa-building', required: true, completed: false }
  ];
  activeTab = 'step1';
  accountsPayableId?: number;
  isSubmitting = false;
  isEditMode = false;
  loading = false;
  error: string | null = null;

  // Dados relacionados
  suppliers: Supplier[] = [];
  costCenters: CostCenter[] = [];
  accountsPayable: AccountsPayable | null = null;

  // Status
  loadingStatuses = false;
  statusOptions: Status[] = [];

  // Opções para selects
  documentTypes = [
    'Nota Fiscal',
    'Fatura',
    'Recibo',
    'Duplicata',
    'Boleto',
    'Contrato',
    'Ordem de Serviço',
    'Conta de Luz',
    'Conta de Água',
    'Conta de Telefone',
    'IPTU',
    'IPVA',
    'Aluguel',
    'Outros'
  ];

  categoryOptions = [
    'Fornecedores',
    'Serviços',
    'Manutenção',
    'Combustível',
    'Seguros',
    'Impostos',
    'Aluguel',
    'Utilidades',
    'Marketing',
    'Material de Escritório',
    'Equipamentos',
    'Treinamento',
    'Consultoria',
    'Outros'
  ];

  /** Status permitidos na edição (pagamento só via "Efetuar Pagamento" na visualização). */
  private readonly paidStatuses = ['pago', 'parcialmente pago'];

  createMode: AccountsPayableCreateMode | null = null;
  showModePicker = false;
  readonly createModeLabels = ACCOUNTS_PAYABLE_CREATE_MODE_LABELS;
  readonly createModeOptions: AccountsPayableCreateMode[] = ['normal', 'parcelado', 'rateio'];

  enableRateio = false;
  costCenterCount = 2;
  costCenterAllocations: CostCenterAllocationRow[] = [
    { costCenterId: null, percent: 0 },
    { costCenterId: null, percent: 0 }
  ];

  batchPreviewRows: AccountsPayableBatchPreviewRow[] = [];
  batchTitleCount = 0;
  /** Vencimentos alterados manualmente na prévia (chave = documentNumber do título). */
  batchDueDateOverrides: Record<string, string> = {};
  editingDueDateDocumentNumber: string | null = null;
  /** Ajusta sábado/domingo para a próxima segunda na prévia e no lote. */
  adjustWeekendsToBusinessDay = false;

  batchPreviewSortKey: BatchPreviewSortKey = 'installment';
  batchPreviewSortDir: 'asc' | 'desc' = 'asc';

  private step2Sub?: Subscription;
  private step3CostCenterSub?: Subscription;

  billingIntervalPresets = [
    { value: '30', label: 'A cada 30 dias (mensal)' },
    { value: '15', label: 'A cada 15 dias' },
    { value: '7', label: 'A cada 7 dias' },
    { value: 'custom', label: 'Personalizado' }
  ];

  private createdBy = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private accountsPayableService: AccountsPayableService,
    private suppliersService: SuppliersService,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private authService: AuthService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.createdBy =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'system';
    this.loadSuppliers();
    this.loadCostCenters();
    this.loadAccountsPayableStatuses();
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.accountsPayableId = +params['id'];
        this.isEditMode = true;
        this.showModePicker = false;
        this.loadAccountsPayable();
      }
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    });

    this.route.queryParams.subscribe(params => {
      if (this.isEditMode) return;
      const mode = params['mode'] as AccountsPayableCreateMode | undefined;
      if (mode === 'normal' || mode === 'parcelado' || mode === 'rateio') {
        this.applyCreateMode(mode);
      } else if (!this.accountsPayableId) {
        this.showModePicker = true;
        this.createMode = null;
      }
    });

    this.setupBatchPreviewRefresh();
  }

  ngOnDestroy(): void {
    this.step2Sub?.unsubscribe();
    this.step3CostCenterSub?.unsubscribe();
  }

  get createModeLabel(): string {
    return this.createMode ? this.createModeLabels[this.createMode] : '';
  }

  get isRateioMode(): boolean {
    return !this.isEditMode && this.createMode === 'rateio';
  }

  get isParceladoMode(): boolean {
    return !this.isEditMode && this.createMode === 'parcelado';
  }

  get isNormalCreateMode(): boolean {
    return !this.isEditMode && this.createMode === 'normal';
  }

  /** Centro de custo no passo 3: pagamento normal e edição (não parcelado nem rateio). */
  get showCostCenterOnStep3(): boolean {
    if (this.isEditMode) return true;
    return this.isNormalCreateMode;
  }

  selectCreateMode(mode: AccountsPayableCreateMode): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode },
      queryParamsHandling: 'merge'
    });
    this.applyCreateMode(mode);
  }

  backToCreateModePicker(): void {
    this.router.navigate(['/financial/accounts-payable']);
  }

  private applyCreateMode(mode: AccountsPayableCreateMode): void {
    this.createMode = mode;
    this.showModePicker = false;
    this.enableRateio = mode === 'rateio';
    this.updateTabLabelsForMode();
    this.updateCostCenterValidatorsForMode();

    if (mode === 'normal') {
      this.formStep2.patchValue({ totalInstallments: 1 }, { emitEvent: false });
    } else if (mode === 'parcelado') {
      const months = Number(this.formStep2.get('totalInstallments')?.value) || 1;
      if (months < 2) {
        this.formStep2.patchValue({ totalInstallments: 2 }, { emitEvent: false });
      }
    } else if (mode === 'rateio') {
      this.costCenterCount = Math.max(2, this.costCenterCount);
      this.syncCostCenterAllocationRows(this.costCenterCount);
      const months = Number(this.formStep2.get('totalInstallments')?.value) || 1;
      if (months < 1) {
        this.formStep2.patchValue({ totalInstallments: 1 }, { emitEvent: false });
      }
    }

    this.scheduleBatchPreviewRefresh();
  }

  private updateCostCenterValidatorsForMode(): void {
    const cc = this.formStep3.get('costCenterId');
    if (!cc) return;
    if (this.isParceladoMode && !this.isEditMode) {
      cc.setValidators([Validators.required]);
    } else {
      cc.clearValidators();
    }
    cc.updateValueAndValidity({ emitEvent: false });
  }

  onParceladoCostCenterChange(): void {
    this.scheduleBatchPreviewRefresh();
  }

  private updateTabLabelsForMode(): void {
    const step2 = this.availableTabs.find(t => t.id === 'step2');
    if (!step2) return;
    if (this.createMode === 'rateio') {
      step2.label = 'Valores, parcelas e divisão';
    } else if (this.createMode === 'parcelado') {
      step2.label = 'Valores e parcelas';
    } else {
      step2.label = 'Valores e vencimento';
    }
  }

  onCostCenterCountChange(raw: number | string): void {
    const n = Math.min(20, Math.max(1, Number(raw) || 1));
    this.costCenterCount = n;
    this.syncCostCenterAllocationRows(n);
    this.scheduleBatchPreviewRefresh();
  }

  onAllocationFieldChange(): void {
    this.scheduleBatchPreviewRefresh();
  }

  /** Soma de % de todas as linhas do rateio (não só linhas “válidas”). */
  get allocationPercentTotal(): number {
    const total = this.costCenterAllocations.reduce(
      (s, r) => s + Math.max(0, Number(r.percent) || 0),
      0
    );
    return Math.round(total * 100) / 100;
  }

  /** Quanto falta para chegar a 100%. */
  get allocationPercentRemaining(): number {
    const diff = 100 - this.allocationPercentTotal;
    return diff > 0.005 ? Math.round(diff * 100) / 100 : 0;
  }

  /** Quanto passou de 100%. */
  get allocationPercentExcess(): number {
    const diff = this.allocationPercentTotal - 100;
    return diff > 0.005 ? Math.round(diff * 100) / 100 : 0;
  }

  get isAllocationPercentExact(): boolean {
    return Math.abs(this.allocationPercentTotal - 100) <= 0.01;
  }

  get allRateioRowsHaveCostCenter(): boolean {
    return this.costCenterAllocations.every(
      r => r.costCenterId != null && Number(r.costCenterId) > 0
    );
  }

  /** Valor líquido informado no passo 2 (base do rateio por mês). Ex.: R$ 160,00. */
  get rateioBaseAmount(): number {
    return this.getNetTotalAmountOnly();
  }

  /** Valores em R$ por linha do rateio (soma = base quando % = 100). */
  get rateioRowAmounts(): number[] {
    const base = this.rateioBaseAmount;
    if (base <= 0) {
      return this.costCenterAllocations.map(() => 0);
    }
    const percents = this.costCenterAllocations.map(r => Math.max(0, Number(r.percent) || 0));
    if (this.isAllocationPercentExact) {
      return splitAmountByPercentages(base, percents);
    }
    return percents.map(p => Math.round(((base * p) / 100) * 100) / 100);
  }

  getAllocationRowAmount(index: number): number {
    return this.rateioRowAmounts[index] ?? 0;
  }

  /** Soma dos valores rateados no mês (deve igualar o valor informado com 100%). */
  get rateioAllocatedMonthTotal(): number {
    return Math.round(this.rateioRowAmounts.reduce((s, v) => s + v, 0) * 100) / 100;
  }

  /** Quanto falta em R$ para o rateio fechar o valor informado. */
  get rateioValueRemaining(): number {
    if (this.isAllocationPercentExact || this.rateioBaseAmount <= 0) {
      return 0;
    }
    const diff = this.rateioBaseAmount - this.rateioAllocatedMonthTotal;
    return diff > 0.005 ? Math.round(diff * 100) / 100 : 0;
  }

  get rateioMonthCount(): number {
    return Math.max(1, Number(this.formStep2.get('totalInstallments')?.value) || 1);
  }

  /** Valor líquido de uma parcela (original − desconto + juros + multa). */
  get batchInstallmentNetAmount(): number {
    return this.getNetTotalAmountOnly();
  }

  /** Total esperado do lote conforme o modo. */
  get batchExpectedLoteTotal(): number {
    const perParcel = this.batchInstallmentNetAmount;
    if (this.isParceladoMode) {
      return Math.round(perParcel * this.parceladoInstallmentCount * 100) / 100;
    }
    if (this.isRateioMode) {
      return Math.round(perParcel * this.rateioMonthCount * 100) / 100;
    }
    return perParcel;
  }

  /** Soma dos valores exibidos na prévia do lote. */
  get batchPreviewAmountTotal(): number {
    return Math.round(this.batchPreviewRows.reduce((s, r) => s + (r.amount || 0), 0) * 100) / 100;
  }

  get parceladoInstallmentCount(): number {
    return Math.max(2, Number(this.formStep2.get('totalInstallments')?.value) || 2);
  }

  get isBatchPreviewAmountMatchingExpected(): boolean {
    if (this.batchInstallmentNetAmount <= 0 || this.batchPreviewRows.length === 0) return true;
    return Math.abs(this.batchPreviewAmountTotal - this.batchExpectedLoteTotal) <= 0.02;
  }

  get hasWeekendDueDatesInPreview(): boolean {
    if (this.adjustWeekendsToBusinessDay) return false;
    const rows = this.isParceladoMode
      ? this.parceladoEsbocoRows
      : this.isRateioMode
        ? this.divisaoEsbocoRows
        : this.batchPreviewRows;
    return rows.some(r => this.showWeekendDueDateWarning(r.dueDate));
  }

  get showBatchDueDateOptions(): boolean {
    return this.showParceladoEsboco || this.showDivisaoEsboco;
  }

  get canBuildBatchPreview(): boolean {
    if (!this.isBatchMode) return false;
    if (this.isRateioMode) {
      return this.isAllocationPercentExact && this.batchInstallmentNetAmount >= 0.01;
    }
    if (this.isParceladoMode) {
      return this.isMultiMonthMode && !!this.formStep2.get('dueDate')?.value;
    }
    return this.batchInstallmentNetAmount >= 0.01;
  }

  /** Exibe o esboço de parcelas/datas no modo parcelado. */
  get showParceladoEsboco(): boolean {
    return this.isParceladoMode && this.isMultiMonthMode && !!this.formStep2.get('dueDate')?.value;
  }

  /** Linhas do esboço: prévia completa ou só cronograma de datas. */
  get parceladoEsbocoRows(): AccountsPayableBatchPreviewRow[] {
    if (!this.showParceladoEsboco) return [];
    if (this.batchPreviewRows.length > 0) {
      return this.decoratePreviewDueDates(this.batchPreviewRows);
    }
    const firstDue = String(this.formStep2.get('dueDate')?.value || '');
    const count = this.parceladoInstallmentCount;
    const dates = buildInstallmentDueDates(firstDue, count, this.resolvedBillingIntervalDays);
    const amt = this.batchInstallmentNetAmount;
    const cc = Number(this.formStep3.get('costCenterId')?.value) || 0;
    const doc = (this.formStep1.get('documentNumber')?.value || 'AP').trim().replace(/\s+/g, '-') || 'AP';
    const synthetic = dates.map((dueDate, i) => ({
      lineNumber: i + 1,
      monthIndex: i + 1,
      monthTotal: count,
      installmentNumber: i + 1,
      installmentTotal: count,
      dueDate,
      costCenterId: cc,
      percent: 0,
      amount: amt,
      documentNumber: `${doc}-${String(i + 1).padStart(3, '0')}`,
      description: `Parcela ${i + 1}/${count}`
    }));
    return this.decoratePreviewDueDates(synthetic);
  }

  private decoratePreviewDueDates(rows: AccountsPayableBatchPreviewRow[]): AccountsPayableBatchPreviewRow[] {
    return rows.map(row => {
      const copy = { ...row };
      const manual = this.batchDueDateOverrides[copy.documentNumber];
      if (manual) {
        copy.dueDate = manual;
        return copy;
      }
      const iso = this.formatDueDateForInput(copy.dueDate);
      if (this.adjustWeekendsToBusinessDay && isWeekendIsoDate(iso)) {
        copy.dueDate = toNextBusinessDayIso(iso);
      }
      return copy;
    });
  }

  get isParceladoEsbocoUsingFullPreview(): boolean {
    return this.batchPreviewRows.length > 0;
  }

  get sortedParceladoEsbocoRows(): AccountsPayableBatchPreviewRow[] {
    return this.sortBatchPreviewRows(this.parceladoEsbocoRows);
  }

  /** Esboço no modo divisão: 1 ou mais parcelas, com data do 1º vencimento. */
  get showDivisaoEsboco(): boolean {
    return this.isRateioMode && !!this.formStep2.get('dueDate')?.value;
  }

  get divisaoEsbocoRows(): AccountsPayableBatchPreviewRow[] {
    if (!this.showDivisaoEsboco) return [];
    if (this.batchPreviewRows.length > 0) {
      return this.decoratePreviewDueDates(this.batchPreviewRows);
    }
    return this.decoratePreviewDueDates(this.buildDivisaoSyntheticEsbocoRows());
  }

  /** Esboço sintético com uma linha por centro de custo (e por mês, se houver vários). */
  private buildDivisaoSyntheticEsbocoRows(): AccountsPayableBatchPreviewRow[] {
    const firstDue = String(this.formStep2.get('dueDate')?.value || '');
    const count = this.rateioMonthCount;
    const dates = buildInstallmentDueDates(firstDue, count, this.resolvedBillingIntervalDays);
    const monthTotal = this.rateioBaseAmount;
    const doc = (this.formStep1.get('documentNumber')?.value || 'AP').trim().replace(/\s+/g, '-') || 'AP';
    const baseDesc = (this.formStep1.get('description')?.value || '').trim();

    const withCc = this.costCenterAllocations.filter(r => Number(r.costCenterId) > 0);
    if (withCc.length === 0) {
      return dates.map((dueDate, i) => {
        const monthIndex = i + 1;
        const cronograma =
          monthTotal > 0
            ? `Total mensal ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(monthTotal)} — Mês ${monthIndex}/${count}`
            : `Mês ${monthIndex}/${count}`;
        return {
          lineNumber: i + 1,
          monthIndex,
          monthTotal: count,
          installmentNumber: 1,
          installmentTotal: 1,
          dueDate,
          costCenterId: 0,
          percent: 0,
          amount: monthTotal,
          documentNumber: count > 1 ? `${doc}-${String(monthIndex).padStart(2, '0')}` : doc,
          description: baseDesc ? `${baseDesc} — ${cronograma}` : cronograma
        };
      });
    }

    const percents = withCc.map(r => Number(r.percent) || 0);
    const splitAmounts =
      monthTotal > 0 ? splitAmountByPercentages(monthTotal, percents) : percents.map(() => 0);

    const rows: AccountsPayableBatchPreviewRow[] = [];
    let line = 0;
    for (let m = 0; m < dates.length; m++) {
      withCc.forEach((alloc, aIndex) => {
        line++;
        const ccId = Number(alloc.costCenterId) || 0;
        const pct = Number(alloc.percent) || 0;
        const amount = splitAmounts[aIndex] ?? 0;
        const monthIndex = m + 1;
        const docSuffix =
          count > 1 || withCc.length > 1
            ? `${doc}-${String(monthIndex).padStart(2, '0')}-CC${ccId}`
            : `${doc}-CC${ccId}`;
        rows.push({
          lineNumber: line,
          monthIndex,
          monthTotal: count,
          installmentNumber: aIndex + 1,
          installmentTotal: withCc.length,
          dueDate: dates[m],
          costCenterId: ccId,
          percent: pct,
          amount,
          documentNumber: docSuffix,
          description: buildDivisaoLineDescription(baseDesc, monthIndex, monthTotal, count, pct, amount)
        });
      });
    }
    return rows;
  }

  get isDivisaoEsbocoUsingFullPreview(): boolean {
    return this.batchPreviewRows.length > 0;
  }

  /** Esboço já lista centros de custo (mesmo antes da prévia oficial do lote). */
  get divisaoEsbocoShowsCcBreakdown(): boolean {
    return this.divisaoEsbocoRows.some(r => Number(r.costCenterId) > 0);
  }

  get showDivisaoEsbocoPercentColumn(): boolean {
    return this.isDivisaoEsbocoUsingFullPreview || this.divisaoEsbocoShowsCcBreakdown;
  }

  get sortedDivisaoEsbocoRows(): AccountsPayableBatchPreviewRow[] {
    return this.sortBatchPreviewRows(this.divisaoEsbocoRows);
  }

  get divisaoEsbocoAmountTotal(): number {
    return Math.round(this.divisaoEsbocoRows.reduce((s, r) => s + (r.amount || 0), 0) * 100) / 100;
  }

  toggleBatchPreviewSort(key: BatchPreviewSortKey): void {
    if (this.batchPreviewSortKey === key) {
      this.batchPreviewSortDir = this.batchPreviewSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.batchPreviewSortKey = key;
      this.batchPreviewSortDir = this.getDefaultSortDirection(key);
    }
  }

  getBatchSortIconClass(key: BatchPreviewSortKey): string {
    if (this.batchPreviewSortKey !== key) return 'fa-sort text-muted opacity-50';
    return this.batchPreviewSortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  private getDefaultSortDirection(key: BatchPreviewSortKey): 'asc' | 'desc' {
    if (key === 'amount') return 'desc';
    return 'asc';
  }

  private sortBatchPreviewRows(rows: AccountsPayableBatchPreviewRow[]): AccountsPayableBatchPreviewRow[] {
    const dir = this.batchPreviewSortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let cmp = 0;
      switch (this.batchPreviewSortKey) {
        case 'document':
          cmp = String(a.documentNumber || '').localeCompare(String(b.documentNumber || ''), 'pt-BR', {
            sensitivity: 'base'
          });
          break;
        case 'dueDate':
          cmp = this.formatDueDateForInput(a.dueDate).localeCompare(this.formatDueDateForInput(b.dueDate));
          break;
        case 'costCenter':
          cmp = this.getCostCenterName(a.costCenterId).localeCompare(
            this.getCostCenterName(b.costCenterId),
            'pt-BR',
            { sensitivity: 'base' }
          );
          break;
        case 'amount':
          cmp = (Number(a.amount) || 0) - (Number(b.amount) || 0);
          break;
        case 'percent':
          cmp = (Number(a.percent) || 0) - (Number(b.percent) || 0);
          break;
        case 'installment':
          cmp = a.monthIndex - b.monthIndex || a.installmentNumber - b.installmentNumber;
          break;
        case 'line':
        default:
          cmp = a.lineNumber - b.lineNumber;
          break;
      }
      return cmp * dir;
    });
  }

  /**
   * Impede que a soma ultrapasse 100% ao editar uma linha.
   */
  onAllocationPercentChange(index: number, raw: number | string): void {
    let value = Math.max(0, Number(raw) || 0);
    const othersTotal = this.costCenterAllocations.reduce((sum, row, i) => {
      if (i === index) return sum;
      return sum + Math.max(0, Number(row.percent) || 0);
    }, 0);
    const maxAllowed = Math.max(0, 100 - othersTotal);
    if (value > maxAllowed) {
      value = Math.round(maxAllowed * 100) / 100;
    }
    this.costCenterAllocations[index].percent = value;
    this.scheduleBatchPreviewRefresh();
  }

  /** Coloca o % que falta na última linha (sem ultrapassar 100%). */
  applyRemainingPercentToLastRow(): void {
    if (this.allocationPercentRemaining <= 0) return;
    const lastIndex = this.costCenterAllocations.length - 1;
    if (lastIndex < 0) return;
    const othersTotal = this.costCenterAllocations.reduce((sum, row, i) => {
      if (i === lastIndex) return sum;
      return sum + Math.max(0, Number(row.percent) || 0);
    }, 0);
    this.costCenterAllocations[lastIndex].percent =
      Math.round(Math.max(0, 100 - othersTotal) * 100) / 100;
    this.scheduleBatchPreviewRefresh();
  }

  /** Divide igualmente 100% entre as linhas do rateio. */
  distributeEqualPercents(): void {
    const n = this.costCenterAllocations.length;
    if (n <= 0) return;
    const base = Math.floor((10000 / n)) / 100;
    let assigned = 0;
    this.costCenterAllocations.forEach((row, i) => {
      if (i === n - 1) {
        row.percent = Math.round((100 - assigned) * 100) / 100;
      } else {
        row.percent = base;
        assigned += base;
      }
    });
    this.scheduleBatchPreviewRefresh();
  }

  get hasUniqueCostCentersInRateio(): boolean {
    const ids = this.costCenterAllocations
      .map(r => Number(r.costCenterId))
      .filter(id => id > 0);
    return ids.length === new Set(ids).size;
  }

  private isRateioStep2Valid(): boolean {
    return (
      this.isAllocationPercentExact &&
      this.allRateioRowsHaveCostCenter &&
      this.hasUniqueCostCentersInRateio &&
      this.getValidAllocations().length > 0
    );
  }

  private syncCostCenterAllocationRows(count: number): void {
    while (this.costCenterAllocations.length < count) {
      this.costCenterAllocations.push({ costCenterId: null, percent: 0 });
    }
    while (this.costCenterAllocations.length > count) {
      this.costCenterAllocations.pop();
    }
  }

  private setupBatchPreviewRefresh(): void {
    this.step2Sub?.unsubscribe();
    this.step2Sub = this.formStep2.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => this.refreshBatchPreview());

    this.step3CostCenterSub?.unsubscribe();
    this.step3CostCenterSub = this.formStep3
      .get('costCenterId')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => {
        if (this.isParceladoMode) {
          this.refreshBatchPreview();
        }
      });
  }

  private scheduleBatchPreviewRefresh(): void {
    setTimeout(() => this.refreshBatchPreview(), 0);
  }

  private refreshBatchPreview(): void {
    if (!this.isBatchMode) {
      this.batchPreviewRows = [];
      this.batchTitleCount = 0;
      return;
    }
    if (!this.canBuildBatchPreview) {
      this.batchPreviewRows = [];
      this.batchTitleCount = 0;
      return;
    }
    const needsAmount = !this.isParceladoMode || this.batchInstallmentNetAmount >= 0.01;
    if (!needsAmount) {
      this.batchPreviewRows = [];
      this.batchTitleCount = 0;
      return;
    }
    try {
      const rows = buildAccountsPayableBatchPreview(this.buildBatchOptions());
      this.applyDueDateOverridesToPreview(rows);
      this.applyWeekendToBusinessDayToPreview(rows);
      this.batchPreviewRows = rows;
      this.batchTitleCount = this.batchPreviewRows.length;
    } catch {
      this.batchPreviewRows = [];
      this.batchTitleCount = 0;
      this.editingDueDateDocumentNumber = null;
    }
  }

  isBatchDueDateOverridden(documentNumber: string): boolean {
    return !!this.batchDueDateOverrides[documentNumber];
  }

  onAdjustWeekendsToBusinessDayChange(enabled: boolean): void {
    this.adjustWeekendsToBusinessDay = enabled;
    this.scheduleBatchPreviewRefresh();
  }

  /** Exibe vermelho só quando ainda é fim de semana e o ajuste automático está desligado. */
  showWeekendDueDateWarning(dueDate: string): boolean {
    return isWeekendIsoDate(this.formatDueDateForInput(dueDate)) && !this.adjustWeekendsToBusinessDay;
  }

  private applyWeekendToBusinessDayToPreview(rows: AccountsPayableBatchPreviewRow[]): void {
    if (!this.adjustWeekendsToBusinessDay) return;
    rows.forEach(row => {
      if (this.isBatchDueDateOverridden(row.documentNumber)) return;
      const iso = this.formatDueDateForInput(row.dueDate);
      if (isWeekendIsoDate(iso)) {
        row.dueDate = toNextBusinessDayIso(iso);
      }
    });
  }

  private resolveItemDueDate(item: AccountsPayableCreate): string {
    const doc = item.documentNumber || '';
    const manual = this.batchDueDateOverrides[doc];
    if (manual) return manual;
    const base = this.formatDueDateForInput(String(item.dueDate || ''));
    if (this.adjustWeekendsToBusinessDay && isWeekendIsoDate(base)) {
      return toNextBusinessDayIso(base);
    }
    return base || String(item.dueDate);
  }

  startEditBatchDueDate(row: AccountsPayableBatchPreviewRow): void {
    this.editingDueDateDocumentNumber = row.documentNumber;
  }

  cancelEditBatchDueDate(): void {
    this.editingDueDateDocumentNumber = null;
  }

  formatDueDateForInput(dueDate: string): string {
    if (!dueDate) return '';
    const s = String(dueDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  onBatchPreviewDueDateChange(row: AccountsPayableBatchPreviewRow, newDate: string): void {
    if (!newDate) return;
    const iso = newDate.slice(0, 10);
    this.batchDueDateOverrides[row.documentNumber] = iso;
    row.dueDate = iso;
    this.editingDueDateDocumentNumber = null;
  }

  resetBatchDueDateOverride(row: AccountsPayableBatchPreviewRow): void {
    delete this.batchDueDateOverrides[row.documentNumber];
    this.editingDueDateDocumentNumber = null;
    this.refreshBatchPreview();
  }

  private applyDueDateOverridesToPreview(rows: AccountsPayableBatchPreviewRow[]): void {
    const keys = new Set(rows.map(r => r.documentNumber));
    Object.keys(this.batchDueDateOverrides).forEach(key => {
      if (!keys.has(key)) {
        delete this.batchDueDateOverrides[key];
      }
    });
    if (
      this.editingDueDateDocumentNumber &&
      !keys.has(this.editingDueDateDocumentNumber)
    ) {
      this.editingDueDateDocumentNumber = null;
    }
    rows.forEach(row => {
      const override = this.batchDueDateOverrides[row.documentNumber];
      if (override) {
        row.dueDate = override;
      }
    });
  }

  private applyDueDateOverridesToPayload(items: AccountsPayableCreate[]): AccountsPayableCreate[] {
    return items.map(item => ({
      ...item,
      dueDate: this.resolveItemDueDate(item)
    }));
  }

  initializeForms(): void {
    this.formStep1 = this.fb.group({
      documentNumber: ['', [Validators.required, Validators.maxLength(50)]],
      documentType: ['', [Validators.required]],
      issueDate: ['', [Validators.required]],
      invoiceNumber: ['', [Validators.maxLength(100)]],
      purchaseOrderNumber: ['', [Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]]
    });

    const today = new Date().toISOString().split('T')[0];

    this.formStep2 = this.fb.group({
      originalAmount: [null, [Validators.required, Validators.min(0.01)]],
      dueDate: [today, [Validators.required]],
      discountAmount: [null, [Validators.min(0)]],
      interestAmount: [null, [Validators.min(0)]],
      fineAmount: [null, [Validators.min(0)]],
      totalInstallments: [1, [Validators.min(1), Validators.max(120)]],
      billingIntervalPreset: ['30'],
      billingIntervalCustomDays: [30, [Validators.min(1), Validators.max(365)]]
    });

    this.formStep3 = this.fb.group({
      supplierId: [''],
      category: ['', [Validators.required]],
      costCenterId: [''],
      status: ['Pendente', [Validators.required]]
    });
  }

  get editStatusOptions(): Status[] {
    return (this.statusOptions || []).filter(
      s => !this.paidStatuses.includes((s.name || '').trim().toLowerCase())
    );
  }

  get isAccountPaidStatus(): boolean {
    const s = (this.accountsPayable?.status || this.formStep3?.get('status')?.value || '')
      .trim()
      .toLowerCase();
    return this.paidStatuses.some(paid => s === paid || s.includes('parcialmente'));
  }

  loadSuppliers(): void {
    this.suppliersService.getAll().subscribe({
      next: (suppliers: Supplier[]) => {
        this.suppliers = suppliers || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar fornecedores:', error);
      }
    });
  }

  loadCostCenters(): void {
    this.costCentersService.getAll().subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar centros de custo:', error);
      }
    });
  }

  loadAccountsPayable(): void {
    if (!this.accountsPayableId) return;

    this.loading = true;
    this.accountsPayableService.getAccountsPayableById(this.accountsPayableId).subscribe({
      next: (accountsPayable) => {
        this.accountsPayable = accountsPayable;
        this.populateForm();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar conta a pagar:', error);
        this.error = 'Erro ao carregar conta a pagar';
        this.loading = false;
      }
    });
  }

  loadAccountsPayableStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('AccountsPayable', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de contas a pagar:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Conta pendente', entityType: 'AccountsPayable', isActive: true },
            { id: 2, name: 'Aprovado', description: 'Conta aprovada', entityType: 'AccountsPayable', isActive: true },
            { id: 3, name: 'Pago', description: 'Conta paga', entityType: 'AccountsPayable', isActive: true },
            { id: 4, name: 'Cancelado', description: 'Conta cancelada', entityType: 'AccountsPayable', isActive: true },
            { id: 5, name: 'Vencido', description: 'Conta vencida', entityType: 'AccountsPayable', isActive: true },
            { id: 6, name: 'Parcialmente Pago', description: 'Conta parcialmente paga', entityType: 'AccountsPayable', isActive: true }
          ] as Status[];
        }
      });
  }

  populateForm(): void {
    if (!this.accountsPayable) return;

    const ap = this.accountsPayable;

    this.formStep1.patchValue({
      documentNumber: ap.documentNumber,
      documentType: ap.documentType,
      issueDate: this.formatDateForInput(ap.issueDate),
      invoiceNumber: ap.invoiceNumber,
      purchaseOrderNumber: ap.purchaseOrderNumber,
      description: ap.description
    });

    this.formStep2.patchValue({
      originalAmount: ap.originalAmount ?? null,
      dueDate: this.formatDateForInput(ap.dueDate),
      discountAmount: ap.discountAmount ?? null,
      interestAmount: ap.interestAmount ?? null,
      fineAmount: ap.fineAmount ?? null,
      totalInstallments: ap.totalInstallments || 1
    });

    this.formStep3.patchValue({
      supplierId: ap.supplierId,
      category: ap.category,
      costCenterId: ap.costCenterId,
      status: ap.status
    });
  }

  formatDateForInput(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().split('T')[0];
  }

  // Getters para facilitar acesso aos campos
  get documentNumber() { return this.formStep1.get('documentNumber'); }
  get documentType() { return this.formStep1.get('documentType'); }
  get originalAmount() { return this.formStep2.get('originalAmount'); }
  get dueDate() { return this.formStep2.get('dueDate'); }
  get category() { return this.formStep3.get('category'); }
  get status() { return this.formStep3.get('status'); }

  get isBatchMode(): boolean {
    if (this.isEditMode) return false;
    if (this.isRateioMode) return true;
    if (this.isParceladoMode) return this.isMultiMonthMode;
    return false;
  }

  get isMultiMonthMode(): boolean {
    return (Number(this.formStep2.get('totalInstallments')?.value) || 1) > 1;
  }

  get resolvedBillingIntervalDays(): number {
    const preset = this.formStep2.get('billingIntervalPreset')?.value;
    if (preset === 'custom') {
      return Math.max(1, Number(this.formStep2.get('billingIntervalCustomDays')?.value) || 1);
    }
    return Math.max(1, Number(preset) || 30);
  }

  getCostCenterName(id: number): string {
    return this.costCenters.find(c => c.id === id)?.name || `CC #${id}`;
  }

  /** Centros de custo ainda não usados em outras linhas do rateio. */
  getCostCentersAvailableForRow(rowIndex: number): CostCenter[] {
    const taken = new Set<number>();
    this.costCenterAllocations.forEach((row, i) => {
      if (i !== rowIndex && row.costCenterId != null && Number(row.costCenterId) > 0) {
        taken.add(Number(row.costCenterId));
      }
    });
    return this.costCenters.filter(cc => !taken.has(cc.id));
  }

  onAllocationCostCenterChange(rowIndex: number, costCenterId: number | null): void {
    if (costCenterId != null && Number(costCenterId) > 0) {
      const duplicate = this.costCenterAllocations.some(
        (row, i) => i !== rowIndex && Number(row.costCenterId) === Number(costCenterId)
      );
      if (duplicate) {
        this.costCenterAllocations[rowIndex].costCenterId = null;
        this.error = 'Este centro de custo já foi selecionado em outra linha.';
        return;
      }
    }
    this.costCenterAllocations[rowIndex].costCenterId = costCenterId;
    this.error = null;
    this.scheduleBatchPreviewRefresh();
  }

  removeAllocationRow(index: number): void {
    if (this.isRateioMode || this.costCenterAllocations.length <= 1) return;
    this.costCenterAllocations.splice(index, 1);
    this.costCenterCount = this.costCenterAllocations.length;
    this.scheduleBatchPreviewRefresh();
  }

  isBillingIntervalCustom(): boolean {
    return this.formStep2.get('billingIntervalPreset')?.value === 'custom';
  }

  getValidAllocations(): CostCenterAllocationRow[] {
    return this.costCenterAllocations.filter(
      r => r.costCenterId != null && r.costCenterId > 0 && (Number(r.percent) || 0) > 0
    );
  }

  private getNetTotalAmountOnly(): number {
    const originalAmount = this.toAmountValue(this.formStep2.get('originalAmount')?.value);
    const discountAmount = this.toAmountValue(this.formStep2.get('discountAmount')?.value);
    const interestAmount = this.toAmountValue(this.formStep2.get('interestAmount')?.value);
    const fineAmount = this.toAmountValue(this.formStep2.get('fineAmount')?.value);
    return originalAmount - discountAmount + interestAmount + fineAmount;
  }

  private buildBaseForBatch(): AccountsPayableCreate {
    const step1Data = this.formStep1.value;
    const step2Data = this.formStep2.value;
    const step3Data = this.formStep3.value;

    const toNumberOrUndefined = (value: unknown): number | undefined => {
      if (value === null || value === undefined || value === '' || value === 'null') {
        return undefined;
      }
      const num = Number(value);
      return Number.isNaN(num) ? undefined : num;
    };

    const today = new Date().toISOString().split('T')[0];

    return {
      documentNumber: (step1Data.documentNumber || '').trim() || 'AP-PREVIEW',
      documentType: step1Data.documentType || 'Outros',
      issueDate: step1Data.issueDate || today,
      dueDate: step2Data.dueDate,
      originalAmount: this.toAmountValue(step2Data.originalAmount),
      discountAmount: toNumberOrUndefined(step2Data.discountAmount),
      interestAmount: toNumberOrUndefined(step2Data.interestAmount),
      fineAmount: toNumberOrUndefined(step2Data.fineAmount),
      amountToPay: this.getNetTotalAmountOnly(),
      status: 'Pendente',
      category: step3Data.category || 'Outros',
      supplierId: toNumberOrUndefined(step3Data.supplierId),
      costCenterId: toNumberOrUndefined(step3Data.costCenterId),
      description: step1Data.description || undefined,
      invoiceNumber: step1Data.invoiceNumber || undefined,
      purchaseOrderNumber: step1Data.purchaseOrderNumber || undefined,
      installmentNumber: 1,
      totalInstallments: toNumberOrUndefined(step2Data.totalInstallments) ?? 1
    };
  }

  private buildBatchOptions() {
    const base = this.buildBaseForBatch();
    const months = Math.max(1, Number(this.formStep2.get('totalInstallments')?.value) || 1);
    const net = this.getNetTotalAmountOnly();
    const valid = this.getValidAllocations();
    const useRateio = this.isRateioMode || (this.enableRateio && valid.length > 0);

    return {
      base,
      firstDueDate: this.formStep2.get('dueDate')?.value,
      monthCount: months,
      intervalDays: this.resolvedBillingIntervalDays,
      monthlyNetAmount: net,
      periodNetAmount: net,
      allocations: valid.map(
        r => ({ costCenterId: r.costCenterId!, percent: Number(r.percent) || 0 })
      ) as CostCenterAllocationInput[],
      useRateio,
      singleCostCenterId: Number(this.formStep3.get('costCenterId')?.value) || undefined,
      fixedAmountPerInstallment: this.isParceladoMode
    };
  }

  /** Vencimento em sábado ou domingo (calendário). */
  isWeekendDueDate(dueDate: string): boolean {
    return isWeekendIsoDate(this.formatDueDateForInput(dueDate));
  }

  private validateBatchBeforeSubmit(): string | null {
    if (!this.isBatchMode) return null;

    if (this.isRateioMode || this.enableRateio) {
      const valid = this.getValidAllocations();
      if (valid.length === 0) {
        return 'Adicione ao menos um centro de custo com percentual na divisão.';
      }
      if (this.allocationPercentExcess > 0) {
        return `A soma dos percentuais não pode passar de 100% (excesso: ${this.allocationPercentExcess.toFixed(2)}%).`;
      }
      if (this.allocationPercentRemaining > 0) {
        return `Faltam ${this.allocationPercentRemaining.toFixed(2)}% para completar 100% na divisão.`;
      }
      if (!this.isAllocationPercentExact) {
        return `A soma dos percentuais da divisão deve ser exatamente 100% (atual: ${this.allocationPercentTotal.toFixed(2)}%).`;
      }
      if (!this.allRateioRowsHaveCostCenter) {
        return 'Selecione o centro de custo em todas as linhas da divisão.';
      }
      if (!this.hasUniqueCostCentersInRateio) {
        return 'Cada centro de custo só pode aparecer uma vez na divisão.';
      }
    } else if (this.isParceladoMode && this.isMultiMonthMode) {
      const cc = Number(this.formStep3.get('costCenterId')?.value);
      if (!cc) {
        return 'Selecione o centro de custo para as parcelas em lote.';
      }
    }

    const count = this.buildBatchPayload().length;
    if (count === 0) {
      return 'Nenhum título gerado para o lote. Verifique parcelas e divisão entre centros de custo.';
    }
    return null;
  }

  private buildBatchPayload(): AccountsPayableCreate[] {
    const items = buildAccountsPayableBatchPayload(this.buildBatchOptions());
    return this.applyDueDateOverridesToPayload(items);
  }

  // Navegação entre steps
  nextStep(): void {
    if (this.currentStep === 2) {
      if (!this.formStep2.valid || !this.isParceladoStep2Complete()) {
        this.markStep2ValueFieldsTouched();
        return;
      }
    }
    if (this.canProceedToNextStep()) {
      if (this.currentStep === 2) {
        this.refreshBatchPreview();
      }
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }

  private isParceladoStep2Complete(): boolean {
    if (!this.isParceladoMode) return true;
    return Number(this.formStep3.get('costCenterId')?.value) > 0;
  }

  private markStep2ValueFieldsTouched(): void {
    ['originalAmount', 'discountAmount', 'interestAmount', 'fineAmount', 'dueDate'].forEach((name) => {
      this.formStep2.get(name)?.markAsTouched();
    });
    if (this.isParceladoMode) {
      this.formStep3.get('costCenterId')?.markAsTouched();
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.formStep1.valid;
      case 2:
        if (!this.formStep2.valid) return false;
        if (this.isParceladoMode && !this.isParceladoStep2Complete()) return false;
        if (this.isRateioMode) {
          return this.isRateioStep2Valid();
        }
        if (this.isBatchMode && this.enableRateio) {
          return this.isAllocationPercentExact;
        }
        return true;
      case 3:
        return this.formStep3.valid;
      default:
        return false;
    }
  }

  isFormValid(): boolean {
    if (!(this.formStep1.valid && this.formStep2.valid && this.formStep3.valid)) {
      return false;
    }
    if (this.isRateioMode) {
      return this.isRateioStep2Valid();
    }
    if (this.isBatchMode && this.enableRateio) {
      return this.isAllocationPercentExact && this.getValidAllocations().length > 0;
    }
    return true;
  }

  // Validação de campos
  isFieldInvalid(fieldName: string, stepNumber: number): boolean {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string, stepNumber: number): string {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    
    if (field?.errors) {
      if (field.errors['required']) return 'Este campo é obrigatório';
      if (field.errors['maxlength']) return `Máximo ${field.errors['maxlength'].requiredLength} caracteres`;
      if (field.errors['min']) {
        const min = field.errors['min'].min;
        if (min === 0.01) return 'Informe um valor maior que zero';
        return `Valor mínimo: ${min}`;
      }
      if (field.errors['email']) return 'Email inválido';
    }
    return '';
  }

  getFormByStep(stepNumber: number): FormGroup {
    switch (stepNumber) {
      case 1: return this.formStep1;
      case 2: return this.formStep2;
      case 3: return this.formStep3;
      default: return this.formStep1;
    }
  }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= this.totalSteps) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }

  /** Converte valor do formulário (null = 0) para cálculo/exibição. */
  private toAmountValue(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  }

  /** Valor líquido de referência (uma parcela/mês). */
  calculateTotalAmount(): number {
    const net = this.getNetTotalAmountOnly();

    if (this.isBatchMode) {
      if (this.batchPreviewRows.length > 0) {
        return this.batchPreviewRows.reduce((s, r) => s + r.amount, 0);
      }
      const months = Number(this.formStep2.get('totalInstallments')?.value) || 1;
      if (this.isParceladoMode) {
        return net * months;
      }
      if (this.isRateioMode || this.enableRateio) {
        return net * months;
      }
      return net;
    }
    return net;
  }

  // Submissão do formulário
  submitForm(): void {
    this.markStep2ValueFieldsTouched();
    if (!this.isFormValid() || this.isSubmitting) return;

    const originalAmount = this.toAmountValue(this.formStep2.get('originalAmount')?.value);
    if (originalAmount < 0.01) {
      this.error = 'Informe o valor original maior que zero.';
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    const batchError = this.validateBatchBeforeSubmit();
    if (batchError) {
      this.error = batchError;
      this.isSubmitting = false;
      return;
    }

    const onSuccess = (): void => {
      this.router.navigate(['/financial/accounts-payable']);
    };
    const onError = (error: { message?: string }): void => {
      console.error('Erro ao salvar conta a pagar:', error);
      this.error = error?.message || 'Erro ao salvar conta a pagar';
      this.isSubmitting = false;
    };

    if (this.isEditMode) {
      this.accountsPayableService
        .updateAccountsPayable(this.prepareFormData() as AccountsPayableUpdate)
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    if (this.isBatchMode) {
      this.accountsPayableService
        .createAccountsPayableBatch(this.buildBatchPayload(), this.createdBy)
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    this.accountsPayableService
      .createAccountsPayable(this.prepareFormData() as AccountsPayableCreate)
      .subscribe({ next: onSuccess, error: onError });
  }

  prepareFormData(): AccountsPayableCreate | AccountsPayableUpdate {
    const step1Data = this.formStep1.value;
    const step2Data = this.formStep2.value;
    const step3Data = this.formStep3.value;

    // Helper function to convert to number or undefined
    const toNumberOrUndefined = (value: any): number | undefined => {
      if (value === null || value === undefined || value === '' || value === 'null') {
        return undefined;
      }
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    };

    const baseData = {
      documentNumber: step1Data.documentNumber,
      documentType: step1Data.documentType,
      issueDate: step1Data.issueDate,
      dueDate: step2Data.dueDate,
      originalAmount: this.toAmountValue(step2Data.originalAmount),
      discountAmount: toNumberOrUndefined(step2Data.discountAmount),
      interestAmount: toNumberOrUndefined(step2Data.interestAmount),
      fineAmount: toNumberOrUndefined(step2Data.fineAmount),
      amountToPay: this.isBatchMode ? this.calculateTotalAmount() : this.getNetTotalAmountOnly(),
      status: this.isEditMode
        ? (this.isAccountPaidStatus ? this.accountsPayable!.status : step3Data.status)
        : 'Pendente',
      category: step3Data.category,
      supplierId: toNumberOrUndefined(step3Data.supplierId),
      costCenterId: toNumberOrUndefined(step3Data.costCenterId),
      description: step1Data.description || undefined,
      invoiceNumber: step1Data.invoiceNumber || undefined,
      purchaseOrderNumber: step1Data.purchaseOrderNumber || undefined,
      installmentNumber:
        this.isEditMode && this.accountsPayable?.installmentNumber != null
          ? this.accountsPayable.installmentNumber
          : 1,
      totalInstallments: toNumberOrUndefined(step2Data.totalInstallments) ?? 1
    };

    if (this.isEditMode && this.accountsPayableId) {
      return {
        id: this.accountsPayableId,
        ...baseData
      } as AccountsPayableUpdate;
    }

    return baseData as AccountsPayableCreate;
  }

  cancel(): void {
    this.router.navigate(['/financial/accounts-payable']);
  }
}
